/**
 * Phase 4c/4d — every piece of CLIENT-SIDE math the `/sizer/` matrix performs,
 * lifted out of `django/sizer/static/sizer/mvc/view/matrix.js` (and
 * `django/static/mvc/universal_fn/universal_fn_tools.js`) so it is readable and
 * independently checkable. Nothing here touches the DOM.
 *
 * PARITY-ONLY: each function is a line-for-line port with its legacy cite. Where
 * legacy is wrong-but-harmless the quirk is reproduced and labelled; the ONE
 * deliberate correction (`interpolateExtrapolate`'s comparator) is proven
 * output-identical against the live payload below.
 */

import type { ISizerDeviation } from 'app/services/sizerApi';

/** `[[fromX, fromY], [toX, toY]]` in BLOCK coordinates, both ends inclusive. */
export type MatrixArea = [[number, number], [number, number]];

// ---------------------------------------------------------------------------
// (a) interpolate_extrapolate — universal_fn_tools.js:824-868
// ---------------------------------------------------------------------------

/**
 * Linear interpolation across an N-dimensional graph, seeking on dimension 0.
 * `matrix.js:109` feeds it `[[airflow, optimal_width, optimal_height], …]` from
 * `/cms/1/v1.0.0/ahu/dynamic_sizer/optimal_sizes/` and the requested airflow.
 *
 * Behaviour ported verbatim (universal_fn_tools.js:835-869):
 *  - a single point returns that point;
 *  - below the first x → extrapolate along points[0]→points[1];
 *  - above the last x  → extrapolate along points[n-2]→points[n-1];
 *  - otherwise interpolate across the bracketing pair (a seeker landing exactly
 *    on a knot yields `f === 0`, i.e. that knot's values unchanged);
 *  - an EMPTY graph throws (`:840-842`);
 *  - falling through the loop returns `undefined` — unreachable while the graph
 *    is sorted, kept so the signature tells the truth.
 *
 * ★ THE ONE DELIBERATE CORRECTION. Legacy sorts with `graph.sort(a => a[0])`
 * (`:839`) — a single-argument comparator, i.e. it ignores `b` entirely and
 * returns a positive number for every real airflow. That is not a sort; it is
 * undefined-ish behaviour that happens to be a no-op for the live payload.
 * VERIFIED 2026-07-28 against the live dev3 response (28 rows, ascending
 * 1000 → 150 000): `Array.from(rows).sort(a => a[0])` is byte-identical to the
 * input AND to `sort((a, b) => a[0] - b[0])`, and both comparators return the
 * same value for airflows 0 / 500 / 20 000 / 20 500 / 33 333 / 200 000. The
 * correct comparator is used here — same output today, right if the API ever
 * returns unsorted rows.
 */
export function interpolateExtrapolate(graph: number[][], seeker: number): number[] | undefined {
  // `:837-838` — the legacy copy-before-sort, so the caller's array is safe.
  const points = Array.from(graph);
  points.sort((a, b) => a[0] - b[0]);

  if (!points.length) throw new Error('The graph was empty'); // :840-842
  if (points.length === 1) return points[0]; // :843-845

  // `:848-851` — interpolate the a→b line at first-dimension = c.
  const interpolate = (a: number[], b: number[], c: number): number[] => {
    const f = (c - a[0]) / (b[0] - a[0]);
    return a.map((_, i) => (b[i] - a[i]) * f + a[i]);
  };

  if (seeker < points[0][0]) return interpolate(points[0], points[1], seeker); // :853-855
  if (seeker > points[points.length - 1][0]) {
    return interpolate(points[points.length - 2], points[points.length - 1], seeker); // :856-862
  }
  for (let i = 0; i < points.length - 1; i++) {
    if (seeker >= points[i][0] && seeker <= points[i + 1][0]) {
      return interpolate(points[i], points[i + 1], seeker); // :863-867
    }
  }
  return undefined;
}

/**
 * `matrix.js:107-108` — the graph `updateOptimals` actually passes: the three
 * columns in that order, with rows where ALL THREE are falsy dropped (`:108`
 * `.filter(el => el[0] || el[1] || el[2])`, so an `airflow: 0` row survives as
 * long as one of the optimal dims is non-zero).
 */
export function buildOptimalGraph(
  rows: Array<{ airflow: number; optimal_width: number; optimal_height: number }>
): number[][] {
  return Array.from(rows.map((el) => [el.airflow, el.optimal_width, el.optimal_height])).filter(
    (el) => el[0] || el[1] || el[2]
  );
}

// ---------------------------------------------------------------------------
// (b) the deviation window — matrix.js:117-128
// ---------------------------------------------------------------------------

export interface DeviationWindow {
  widthMax: number;
  widthMin: number;
  heightMax: number;
  heightMin: number;
}

/**
 * The ±% band around the server's optimal OUTSIDE dimensions, inside which a
 * cell earns `.activeBorder`. `deviation_*` are PERCENTAGES (dev3: all ±1).
 *
 * VERIFIED end-to-end on dev3 2026-07-28 — airflow 20 000
 *   → `optimal [20000, 80, 90]`
 *   → response `width 84.208, height 92.144`
 *   → ceil(84.208 × 1.01) = 86, floor(84.208 × 0.99) = 83,
 *     ceil(92.144 × 1.01) = 94, floor(92.144 × 0.99) = 91
 *   → the STRICT `<`/`>` test in `renderBlock` leaves exactly four cells:
 *     w84/85 × h92/93. Observed: `.activeBorder` = w84.00_h92.00, w84.00_h93.00,
 *     w85.00_h92.00, w85.00_h93.00. ✔
 */
export function deviationWindow(
  width: number,
  height: number,
  deviation: ISizerDeviation
): DeviationWindow {
  return {
    widthMax: Math.ceil((width * (100 + deviation.deviation_width_max)) / 100), // :117-119
    widthMin: Math.floor((width * (100 + deviation.deviation_width_min)) / 100), // :120-122
    heightMax: Math.ceil((height * (100 + deviation.deviation_height_max)) / 100), // :123-125
    heightMin: Math.floor((height * (100 + deviation.deviation_height_min)) / 100), // :126-128
  };
}

// ---------------------------------------------------------------------------
// (c) scrollToTheMostOptimal's index snap — matrix.js:170-177 / :192-199
// ---------------------------------------------------------------------------

/**
 * The 0.25 / 0.75 snap, applied identically to X and Y.
 *
 * A fractional part outside [0.25, 0.75] snaps to the NEAREST cell centre
 * (`Math.round`); anything inside it snaps to the BOUNDARY between two cells
 * (`floor + 0.5`) so the viewport is centred on the seam rather than on either
 * neighbour. Callers then add the further `+0.5` of `:204` / `:208` to convert a
 * cell index into that cell's centre in pixels.
 */
export function snapOptimalIndex(index: number): number {
  const frac = index - Math.floor(index);
  return frac < 0.25 || frac > 0.75 ? Math.round(index) : Math.floor(index) + 0.5;
}

/**
 * `matrix.js:157-168` / `:179-190` — the unsnapped axis index of the most
 * optimal cell: `(mostOptimal - min) / increment`, clamped to
 * `[0, floor((max - min) / increment)]`.
 *
 * ⚠ Legacy clamps against `floor((max - min) / increment)`, NOT against
 * `values.length - 1`. For construction class 5 (`width_increment: 2`) both are
 * 138, so they agree; the formula is kept verbatim regardless.
 */
export function optimalAxisIndex(
  mostOptimal: number,
  min: number,
  max: number,
  increment: number
): number {
  return Math.max(0, Math.min((mostOptimal - min) / increment, Math.floor((max - min) / increment)));
}

// ---------------------------------------------------------------------------
// (d) the windowing area algebra — matrix.js:344-385
// ---------------------------------------------------------------------------

/** `cut_areas` (matrix.js:360-372) — the intersection, or `null` if disjoint. */
export function cutAreas(area1: MatrixArea, area2: MatrixArea): MatrixArea | null {
  const cut: MatrixArea = [
    [Math.max(area1[0][0], area2[0][0]), Math.max(area1[0][1], area2[0][1])],
    [Math.min(area1[1][0], area2[1][0]), Math.min(area1[1][1], area2[1][1])],
  ];
  return cut[0][0] <= cut[1][0] && cut[0][1] <= cut[1][1] ? cut : null;
}

/**
 * `shrink_area_to_fit_available_space` (matrix.js:344-356) — clamp to the real
 * grid, `[[0,0], [width_values.length - 1, height_values.length - 1]]`.
 */
export function shrinkAreaToFitAvailableSpace(
  from: [number, number],
  to: [number, number],
  widthCount: number,
  heightCount: number
): MatrixArea | null {
  return cutAreas(
    [from, to],
    [
      [0, 0],
      [widthCount - 1, heightCount - 1],
    ]
  );
}

/**
 * `enlarge_area` (matrix.js:375-385) — grow by `dist` on every side, then
 * (when `safe`) re-clamp to the grid. Returns `undefined` for a null input,
 * exactly like legacy's bare `if (!area) return;`.
 */
export function enlargeArea(
  area: MatrixArea | null,
  dist: number,
  widthCount: number,
  heightCount: number,
  safe = true
): MatrixArea | null | undefined {
  if (!area) return undefined;
  const result: MatrixArea = [
    [area[0][0] - dist, area[0][1] - dist],
    [area[1][0] + dist, area[1][1] + dist],
  ];
  return safe
    ? shrinkAreaToFitAvailableSpace(result[0], result[1], widthCount, heightCount)
    : result;
}

// ---------------------------------------------------------------------------

/** `fn.tools.timeout` (sizer/mvc/fn/tools.js:94-98) — the yield between rows. */
export function timeout(msecs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, msecs);
  });
}
